/**
 * Object Library — persistent storage of geometry snapshots.
 *
 * Entries are scene-independent and stored globally in localStorage
 * under a single key. Each entry is a frozen copy of a SceneObject's
 * geometry, topology, and material at the time of capture.
 */

import {
  base64ToFloat32Array,
  base64ToUint8Array,
  base64ToUint32Array,
  typedArrayToBase64,
  uint8ArrayToBase64,
} from "../app/encoding.ts";
import { buildEdgesAndCornerEdges } from "./topology.ts";
import type {
  PolygonTopology,
  RenderTriangleIndices,
  SceneMaterial,
} from "./types.ts";
import { asRenderTriangleIndices } from "./types.ts";

const STORAGE_KEY = "poseref_object_library";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LibraryEntry {
  id: string;
  name: string;
  createdAt: number;
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  normals: Float32Array;
  topology: PolygonTopology;
  uvs?: Float32Array;
  material: SceneMaterial;
}

interface SerializedEntry {
  id: string;
  name: string;
  createdAt: number;
  vertices: string;
  indices: string;
  normals: string;
  topologyOffsets: string;
  topologyCorners: string;
  topologyEdges?: string;
  topologyCornerEdges?: string;
  topologyEdgeFlags?: string;
  uvs?: string;
  material: SceneMaterial;
}

// ---------------------------------------------------------------------------
// ID generation
// ---------------------------------------------------------------------------

let _libId = 1;

export function createLibraryEntryId(): string {
  return `lib_${_libId++}_${Date.now().toString(36)}`;
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

export function loadLibrary(): LibraryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const entries = JSON.parse(raw) as SerializedEntry[];
    // Discard old entries without topology
    return entries
      .filter((s) => s.topologyOffsets != null && s.topologyCorners != null)
      .map(deserializeEntry);
  } catch {
    return [];
  }
}

export function saveLibrary(entries: LibraryEntry[]): void {
  try {
    const serialized = entries.map(serializeEntry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function serializeEntry(entry: LibraryEntry): SerializedEntry {
  return {
    id: entry.id,
    name: entry.name,
    createdAt: entry.createdAt,
    vertices: typedArrayToBase64(entry.vertices),
    indices: typedArrayToBase64(entry.indices),
    normals: typedArrayToBase64(entry.normals),
    topologyOffsets: typedArrayToBase64(entry.topology.offsets),
    topologyCorners: typedArrayToBase64(entry.topology.corners),
    topologyEdges: typedArrayToBase64(entry.topology.edges),
    topologyCornerEdges: typedArrayToBase64(entry.topology.cornerEdges),
    topologyEdgeFlags: uint8ArrayToBase64(entry.topology.edgeFlags),
    uvs: entry.uvs ? typedArrayToBase64(entry.uvs) : undefined,
    material: { ...entry.material },
  };
}

function deserializeEntry(s: SerializedEntry): LibraryEntry {
  return {
    id: s.id,
    name: s.name,
    createdAt: s.createdAt,
    vertices: base64ToFloat32Array(s.vertices),
    indices: asRenderTriangleIndices(base64ToUint32Array(s.indices)),
    normals: base64ToFloat32Array(s.normals),
    topology: (() => {
      const offsets = base64ToUint32Array(s.topologyOffsets);
      const corners = base64ToUint32Array(s.topologyCorners);
      const edgeData = s.topologyEdges
        ? {
            edges: base64ToUint32Array(s.topologyEdges),
            cornerEdges: base64ToUint32Array(s.topologyCornerEdges!),
            edgeFlags: new Uint8Array(base64ToUint8Array(s.topologyEdgeFlags!)),
          }
        : buildEdgesAndCornerEdges(offsets, corners);
      return { offsets, corners, ...edgeData };
    })(),
    uvs: s.uvs ? base64ToFloat32Array(s.uvs) : undefined,
    material: { ...s.material },
  };
}
